import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CHAKRAS } from "../data/chakras";

const VITRUVIAN = "/illuminated-being.png";

// Sacred-geometry point helpers (hexagonal lattice).
const hexRing = (dist, offset = 0) =>
  Array.from({ length: 6 }).map((_, k) => {
    const a = offset + (k * Math.PI) / 3;
    return [dist * Math.cos(a), dist * Math.sin(a)];
  });
const SEED_R = 34;
const SEED_CW_COLOR = "#FF2D55"; // root red — clockwise (back) seed
const SEED_CCW_COLOR = "#5AC8FA"; // throat blue — counter-clockwise (front) seed
const SEED_PTS = [[0, 0], ...hexRing(SEED_R)];
const FLOWER_R = 22;
const FLOWER_PTS = [
  [0, 0],
  ...hexRing(FLOWER_R),
  ...hexRing(FLOWER_R * Math.sqrt(3), Math.PI / 6),
  ...hexRing(FLOWER_R * 2),
];

// Slowly rotating mandala: Seed of Life while a chakra is active, blooming into a
// zoomed-in Flower of Life on completion. `geometry` is "seed" | "flower".
const Mandala = ({ color, active, geometry = "seed" }) => {
  // Seeds are red (cw) / blue (ccw) by default, but adopt the engaged chakra colour when active.
  const cwColor = active ? color : SEED_CW_COLOR;
  const ccwColor = active ? color : SEED_CCW_COLOR;
  return (
    <motion.div
      aria-hidden
      className="absolute left-1/2 pointer-events-none"
      style={{ width: "187.4%", height: "125.72%", top: "50.05%", x: "-50%", y: "-50%" }}
      animate={{ scale: active ? 1.01 : 1.0, opacity: active ? 0.85 : 0.4 }}
      transition={{
        scale: { duration: 3, ease: "easeInOut" },
        opacity: { duration: 3, ease: "easeInOut" },
      }}
    >
      <svg viewBox="-100 -100 200 200" className="h-full w-full" style={{ filter: `drop-shadow(0 0 12px ${color})` }}>
        {/* Back Seed of Life — rotates counterclockwise (blue by default) */}
        <g className="mandala-spin-ccw" stroke={ccwColor} fill="none" strokeWidth="0.8" opacity="0.45">
          {SEED_PTS.map(([x, y], i) => (
            <circle key={`a${i}`} cx={x} cy={y} r={SEED_R} />
          ))}
        </g>
        {/* Front Seed of Life — rotates clockwise (red by default; CSS animation for env consistency) */}
        <motion.g
          animate={{ opacity: geometry === "flower" ? 0 : 0.9 }}
          transition={{ opacity: { duration: 0.6, ease: "easeInOut" } }}
        >
          <g className="mandala-spin-cw" stroke={cwColor} fill="none" strokeWidth="1.4">
            {SEED_PTS.map(([x, y], i) => (
              <circle
                key={`s${i}`}
                cx={x}
                cy={y}
                r={SEED_R}
                opacity={i === 0 ? 1 : 0.4 + 0.55 * ((6 - i) / 5)}
              />
            ))}
          </g>
        </motion.g>
        {/* Flower of Life — blooms from center (scale) with a counterclockwise spin (inner group) */}
        <motion.g
          initial={false}
          animate={{
            opacity: geometry === "flower" ? 0.95 : 0,
            scale: geometry === "flower" ? (SEED_R * 2) / (FLOWER_R * 3) : 0, // outer ring meets the Seed of Life's outer edge
          }}
          transition={{
            opacity: { duration: 1, ease: "easeInOut" },
            scale: { duration: 2.6, ease: "easeInOut" },
          }}
        >
          <g className="mandala-spin-ccw" stroke={color} fill="none" strokeWidth="0.9">
            <circle r={FLOWER_R * 3} opacity="0.5" />
            {FLOWER_PTS.map(([x, y], i) => (
              <circle key={`f${i}`} cx={x} cy={y} r={FLOWER_R} />
            ))}
          </g>
        </motion.g>
      </svg>
    </motion.div>
  );
};

const PETAL = "M0,-22 C7,-32 7,-41 0,-48 C-7,-41 -7,-32 0,-22 Z";

function Yantra({ symbol }) {
  const white = "rgba(255,255,255,0.96)";
  if (symbol === "root")
    return (
      <g>
        <rect x="-12" y="-12" width="24" height="24" rx="1.5" />
        <path d="M-9,-6 L9,-6 L0,9 Z" />
      </g>
    );
  if (symbol === "crescent")
    return <path d="M8,-11 A12,12 0 1 0 8,11 A9,9 0 1 1 8,-11 Z" fill={white} stroke="none" />;
  if (symbol === "triangle") return <path d="M-12,-8 L12,-8 L0,12 Z" />;
  if (symbol === "hexagram")
    return (
      <g>
        <path d="M0,-13 L11,6 L-11,6 Z" />
        <path d="M0,13 L11,-6 L-11,-6 Z" />
      </g>
    );
  if (symbol === "circle-triangle")
    return (
      <g>
        <path d="M-12,-9 L12,-9 L0,11 Z" />
        <circle cx="0" cy="-1" r="5.5" />
      </g>
    );
  // om
  return (
    <text
      x="0"
      y="0"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize="24"
      fill={white}
      stroke="none"
      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700 }}
    >
      &#2384;
    </text>
  );
}

function ChakraGlyph({ chakra, orbSize, showPetals }) {
  const n = chakra.petals;
  const s = orbSize * 2.6;
  const offset = n === 2 ? 90 : 0;
  return (
    <svg
      width={s}
      height={s}
      viewBox="-50 -50 100 100"
      className="absolute left-1/2 top-1/2 pointer-events-none"
      style={{ transform: "translate(-50%, -50%)", overflow: "visible" }}
    >
      {/* Lotus petals bloom only on completion of this chakra */}
      <AnimatePresence>
        {showPetals && (
          <motion.g
            key="petals"
            fill={chakra.glow}
            stroke={chakra.color}
            strokeWidth="1"
            style={{ transformOrigin: "0px 0px" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 0.92, scale: 1 }}
            exit={{ opacity: 0, scale: 0, transition: { duration: 0.5, ease: "easeIn" } }}
            transition={{ duration: 2.6, ease: "easeInOut" }}
          >
            {Array.from({ length: n }).map((_, i) => (
              <path key={i} d={PETAL} transform={`rotate(${offset + (360 / n) * i})`} />
            ))}
          </motion.g>
        )}
      </AnimatePresence>
      <motion.g
        fill="none"
        stroke="rgba(255,255,255,0.96)"
        strokeWidth="2"
        strokeLinejoin="round"
        style={{ filter: "drop-shadow(0 0 2px rgba(255,255,255,0.7))", transformOrigin: "0px 0px" }}
        animate={{ scale: showPetals ? 2.4 : 1 }}
        transition={{ duration: 2.6, ease: "easeInOut" }}
      >
        <Yantra symbol={chakra.symbol} />
      </motion.g>
    </svg>
  );
}

export default function IlluminatedBeing({
  activeChakraId,
  onChakraClick,
  breathScale = 1,
  auraColor = "rgba(120,150,255,0.5)",
  healingProgress = -1, // index up to which channel is lit; -1 = off
  geometry = "seed",
  bloom = false, // when true, the active chakra shows its lotus petals (completion)
  afterglow = false, // gentle full-body bloom of light when all seven complete
}) {
  return (
    <div
      className="relative mx-auto"
      style={{ width: "min(72vw, 380px)", aspectRatio: "848 / 1264" }}
      data-testid="illuminated-being"
    >
      {/* rotating sacred-geometry mandala */}
      <Mandala color={auraColor} active={!!activeChakraId} geometry={geometry} />

      {/* breathing aura */}
      <motion.div
        aria-hidden
        className="absolute left-1/2 rounded-full pointer-events-none"
        style={{
          width: "132%",
          height: "94%",
          top: "49%",
          x: "-50%",
          y: "-50%",
          background: `radial-gradient(ellipse at center, ${auraColor} 0%, rgba(10,10,30,0) 68%)`,
          filter: "blur(26px)",
        }}
        animate={{ scale: breathScale, opacity: [0.4, 0.7, 0.4] }}
        transition={{ scale: { duration: 0.6 }, opacity: { duration: 6, repeat: Infinity, ease: "easeInOut" } }}
      />

      {/* Vitruvian being — transparent PNG (black keyed to alpha) so the starfield shows through */}
      <img
        src={VITRUVIAN}
        alt="Vitruvian illuminated being"
        className="absolute inset-0 h-full w-full object-cover pointer-events-none select-none"
        draggable={false}
      />

      {/* Journey afterglow — a gentle full-body bloom of light on completing all seven */}
      <AnimatePresence>
        {afterglow && (
          <React.Fragment>
            {/* soft radiant halo hugging the whole body */}
            <motion.div
              aria-hidden
              className="absolute left-1/2 top-1/2 rounded-full pointer-events-none"
              style={{
                width: "128%",
                height: "104%",
                x: "-50%",
                y: "-50%",
                background:
                  "radial-gradient(ellipse at center, rgba(255,255,255,0.6) 0%, rgba(199,125,255,0.3) 42%, rgba(90,200,250,0.14) 64%, rgba(3,3,8,0) 82%)",
                filter: "blur(26px)",
                mixBlendMode: "screen",
              }}
              initial={{ opacity: 0, scale: 0.55 }}
              animate={{ opacity: [0, 0.95, 0.7], scale: [0.55, 1.12, 1] }}
              exit={{ opacity: 0, scale: 1.2 }}
              transition={{ duration: 2.6, ease: "easeOut" }}
              data-testid="afterglow-halo"
            />
            {/* glowing white silhouette bloom (brightened duplicate of the figure) */}
            <motion.img
              src={VITRUVIAN}
              aria-hidden
              alt=""
              className="absolute inset-0 h-full w-full object-cover pointer-events-none select-none"
              style={{ filter: "brightness(2.4) saturate(0) blur(2px)", mixBlendMode: "screen" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.85, 0.45] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2.8, ease: "easeInOut" }}
              draggable={false}
            />
          </React.Fragment>
        )}
      </AnimatePresence>

      {/* Healing ascending beam overlay */}
      {healingProgress >= 0 && (
        <motion.div
          aria-hidden
          className="absolute left-1/2 -translate-x-1/2 pointer-events-none rounded-full"
          style={{
            width: 5,
            bottom: "17%",
            background: "linear-gradient(to top, #FF2D55, #FFCC00, #34C759, #5AC8FA, #C77DFF)",
            boxShadow: "0 0 22px 6px rgba(200,180,255,0.8)",
          }}
          animate={{ height: `${Math.max(0, (healingProgress + 1) / CHAKRAS.length) * 64}%` }}
          transition={{ duration: 1.1, ease: "easeInOut" }}
        />
      )}

      {/* Chakra orbs */}
      {CHAKRAS.map((c) => {
        const active = activeChakraId === c.id;
        return (
          <div
            key={c.id}
            className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ top: `${c.top}%` }}
          >
            <motion.button
              data-testid={`chakra-orb-${c.id}`}
              onClick={() => onChakraClick(c)}
              className="chakra-orb relative flex items-center justify-center rounded-full outline-none"
              style={{ width: active ? 34 : 24, height: active ? 34 : 24 }}
              whileHover={{ scale: 1.22 }}
              whileTap={{ scale: 0.92 }}
              animate={{ scale: active ? [1, 1.14, 1] : 1 }}
              transition={active ? { duration: 2.4, repeat: Infinity, ease: "easeInOut" } : { duration: 0.3 }}
              aria-label={c.english}
            >
              <span
                className="absolute inset-0 rounded-full"
                style={{
                  background: `radial-gradient(circle at 35% 30%, #fff 0%, ${c.color} 45%, ${c.color} 100%)`,
                  boxShadow: `0 0 ${active ? 42 : 20}px ${active ? 14 : 6}px ${c.glow}`,
                }}
              />
              <ChakraGlyph chakra={c} orbSize={active ? 34 : 24} showPetals={bloom && active} />
            </motion.button>
          </div>
        );
      })}
    </div>
  );
}
